"use client";

import "@/shared/styles/carevinco-public-shell.scss";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import type { AuthDialogMode, RegistrationRole } from "@/modules/auth/components/AuthDialog";
import { CorrectedMarketingFooter } from "@/modules/landing/components/CorrectedMarketingFooter";
import { InquiryModal } from "@/modules/landing/components/InquiryModal";
import { LandingHeader } from "@/modules/landing/components/LandingHeader";
import AppStartupScreen from "@/shared/components/AppStartupScreen";
import { useAppAuthSnapshot } from "@/shared/providers/AppAuthSnapshot";
import type { AppRole } from "@/shared/store/authStore";

const PublicAuthDialog = lazy(() => import("@/modules/landing/components/PublicAuthDialog"));
const ProviderWireframeFooter = lazy(() => import("@/modules/landing/components/ProviderWireframeFooter"));
const LegacyWireframeFooter = lazy(() => import("@/modules/landing/components/LegacyWireframeFooter"));

const GOOGLE_PLAY_URL =
  process.env.NEXT_PUBLIC_GOOGLE_PLAY_URL || "https://play.google.com/store/search?q=Carevinco&c=apps";
const APP_STORE_URL =
  process.env.NEXT_PUBLIC_APP_STORE_URL || "https://apps.apple.com/us/iphone/search?term=Carevinco";

type MarketingVariant = "legacy" | "corrected";

function preferredScrollBehavior(): ScrollBehavior {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
}

function useAppShellReady() {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let fallbackTimer: number | undefined;

    async function prepareShell() {
      const criticalAssets = [
        "/assets/brand/logo-mark-7cc959d9-5283-46c3-92ec-5f8be3d1ffea.svg",
        "/assets/brand/logo-type-493879ef-c62f-4114-b3fe-6882487aadbc.svg",
        "/assets/landingHeader/portal-icon-e066ad3f-9b90-4373-be93-cecb5fa71eec.svg",
      ];
      const assetReadiness = Promise.all(
        criticalAssets.map(
          (source) =>
            new Promise<void>((resolve) => {
              const image = new window.Image();
              image.onload = () => resolve();
              image.onerror = () => resolve();
              image.src = source;
              if (image.complete) resolve();
            }),
        ),
      );
      const fontReadiness = "fonts" in document ? document.fonts.ready : Promise.resolve();

      await Promise.race([
        Promise.all([fontReadiness, assetReadiness]),
        new Promise<void>((resolve) => {
          fallbackTimer = window.setTimeout(resolve, 1_500);
        }),
      ]).catch(() => undefined);
      if (fallbackTimer != null) {
        window.clearTimeout(fallbackTimer);
        fallbackTimer = undefined;
      }
      await new Promise<void>((resolve) => {
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => resolve()));
      });

      if (!cancelled) setIsReady(true);
    }

    void prepareShell();
    return () => {
      cancelled = true;
      if (fallbackTimer != null) window.clearTimeout(fallbackTimer);
    };
  }, []);

  return isReady;
}

function useLandingAuthDialog() {
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [authDialogMode, setAuthDialogMode] = useState<AuthDialogMode>("login");
  const [authDialogRole, setAuthDialogRole] = useState<RegistrationRole>("elderly");

  const openAuthDialog = useCallback((mode: AuthDialogMode, role: RegistrationRole = "elderly") => {
    setAuthDialogMode(mode);
    setAuthDialogRole(role);
    setAuthDialogOpen(true);
  }, []);

  return {
    authDialogMode,
    authDialogOpen,
    authDialogRole,
    closeAuthDialog: useCallback(() => setAuthDialogOpen(false), []),
    openAuthDialog,
  };
}

function LandingAuthQuerySync({
  onAuthRequest,
}: {
  onAuthRequest: (mode: AuthDialogMode, role?: RegistrationRole) => void;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const searchParamsValue = useSearchParams().toString();

  useEffect(() => {
    const currentParams = new URLSearchParams(searchParamsValue);
    const authMode = currentParams.get("auth") as AuthDialogMode | null;

    if (authMode !== "login" && authMode !== "register" && authMode !== "forgot") return;

    onAuthRequest(authMode, currentParams.get("role") === "provider" ? "provider" : "elderly");
    currentParams.delete("auth");
    currentParams.delete("role");
    const nextQuery = currentParams.toString();
    router.replace(`${pathname}${nextQuery ? `?${nextQuery}` : ""}${window.location.hash}`, { scroll: false });
  }, [onAuthRequest, pathname, router, searchParamsValue]);

  return null;
}

function scrollMarketingHashIntoView(hash: string) {
  const target = document.getElementById(hash.replace("#", ""));
  if (!target) return false;

  const header = document.querySelector(
    ".cv-wireframe .site-header, .cv-wireframe [data-marketing-header]",
  ) as HTMLElement | null;
  const targetTop = window.scrollY + target.getBoundingClientRect().top - (header?.getBoundingClientRect().height ?? 0) - 10;
  window.scrollTo({ top: Math.max(targetTop, 0), behavior: preferredScrollBehavior() });
  return true;
}

function useWireframeInteractions(rootRef: RefObject<HTMLElement | null>, openInquiry: () => void) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    function handleClick(event: MouseEvent) {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      if (target.closest("[data-open-inquiry]")) {
        event.preventDefault();
        openInquiry();
        return;
      }

      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      const href = anchor?.getAttribute("href") ?? "";
      const isLocalHash = href.startsWith("#") || (href.startsWith("/#") && window.location.pathname === "/");
      if (anchor && isLocalHash) {
        const hash = href.startsWith("/#") ? href.slice(1) : href;
        if (scrollMarketingHashIntoView(hash)) {
          event.preventDefault();
          window.history.pushState(null, "", href.startsWith("/#") ? href : `${window.location.pathname}${hash}`);
          return;
        }
      }

      const faqButton = target.closest("[data-accordion] .faq-item button");
      const item = faqButton?.closest(".faq-item");
      const accordion = faqButton?.closest("[data-accordion]");
      if (item && accordion) {
        accordion.querySelectorAll(".faq-item").forEach((node) => {
          if (node !== item) node.classList.remove("is-open");
        });
        item.classList.toggle("is-open");
      }
    }

    root.addEventListener("click", handleClick);
    return () => root.removeEventListener("click", handleClick);
  }, [openInquiry, rootRef]);
}

function BackToTopButton() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    function updateVisibility() {
      const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
      setVisible(scrollableHeight > 0 && window.scrollY / scrollableHeight >= 0.3);
    }

    updateVisibility();
    window.addEventListener("scroll", updateVisibility, { passive: true });
    window.addEventListener("resize", updateVisibility);
    return () => {
      window.removeEventListener("scroll", updateVisibility);
      window.removeEventListener("resize", updateVisibility);
    };
  }, []);

  return (
    <button
      aria-label="Back to Top"
      className={`back-to-top${visible ? " is-visible" : ""}`}
      type="button"
      onClick={() => window.scrollTo({ top: 0, behavior: preferredScrollBehavior() })}
    >
      <span aria-hidden="true" className="back-to-top__icon">↑</span>
      <span className="back-to-top__label">Back to Top</span>
    </button>
  );
}

function WireframeFooter({ role, showInquiry }: { role?: AppRole | null; showInquiry?: boolean } = {}) {
  if (role === "provider") {
    return (
      <Suspense fallback={null}>
        <ProviderWireframeFooter appStoreUrl={APP_STORE_URL} googlePlayUrl={GOOGLE_PLAY_URL} showInquiry={showInquiry} />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={null}>
      <LegacyWireframeFooter appStoreUrl={APP_STORE_URL} googlePlayUrl={GOOGLE_PLAY_URL} role={role} showInquiry={showInquiry} />
    </Suspense>
  );
}

function WireframeInteractiveShell({
  children,
  includeBackToTop = true,
  variant = "legacy",
}: {
  children: ReactNode;
  includeBackToTop?: boolean;
  variant?: MarketingVariant;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const openInquiry = useCallback(() => setInquiryOpen(true), []);
  const closeInquiry = useCallback(() => setInquiryOpen(false), []);
  useWireframeInteractions(rootRef, openInquiry);

  return (
    <div className={`cv-wireframe${variant === "corrected" ? " corrected-public-pages" : ""}`} ref={rootRef}>
      {children}
      <InquiryModal open={inquiryOpen} onClose={closeInquiry} />
      {includeBackToTop ? <BackToTopButton /> : null}
    </div>
  );
}

function WireframePageChrome({ children, variant = "legacy" }: { children: ReactNode; variant?: MarketingVariant }) {
  const appShellReady = useAppShellReady();
  const { hasHydrated, isAuthenticated, isAuthRouting, isLogoutPending, role } = useAppAuthSnapshot();
  const router = useRouter();
  const { authDialogMode, authDialogOpen, authDialogRole, closeAuthDialog, openAuthDialog } = useLandingAuthDialog();

  useEffect(() => {
    if (hasHydrated && isLogoutPending && !isAuthenticated) router.replace("/#home");
  }, [hasHydrated, isAuthenticated, isLogoutPending, router]);

  if (hasHydrated && isAuthenticated && !appShellReady) {
    return <AppStartupScreen message="Preparing your Carevinco experience..." title="Loading Carevinco" />;
  }
  if (hasHydrated && isAuthRouting) {
    return <AppStartupScreen message="Preparing your secure workspace..." title="Loading Carevinco" />;
  }
  if (hasHydrated && isLogoutPending) {
    return <AppStartupScreen message="Taking you to the Carevinco home page..." title="Signing you out" />;
  }

  return (
    <WireframeInteractiveShell variant={variant}>
      <Suspense fallback={null}><LandingAuthQuerySync onAuthRequest={openAuthDialog} /></Suspense>
      <div className={`page-shell page-shell--with-header${variant === "corrected" ? " corrected-public-shell" : ""}`}>
        <LandingHeader authDialogOpen={authDialogOpen} onAuthRequest={openAuthDialog} />
        {children}
        {variant === "corrected" && !isAuthenticated ? (
          <CorrectedMarketingFooter appStoreUrl={APP_STORE_URL} googlePlayUrl={GOOGLE_PLAY_URL} />
        ) : (
          <WireframeFooter role={isAuthenticated ? role : undefined} showInquiry={!isAuthenticated} />
        )}
      </div>
      {authDialogOpen ? (
        <Suspense fallback={null}>
          <PublicAuthDialog initialMode={authDialogMode} initialRole={authDialogRole} onClose={closeAuthDialog} />
        </Suspense>
      ) : null}
    </WireframeInteractiveShell>
  );
}

export function MarketingPageShell({ children, variant = "legacy" }: { children: ReactNode; variant?: MarketingVariant }) {
  return <WireframePageChrome variant={variant}>{children}</WireframePageChrome>;
}

export function LandingFooter({
  embedded = false,
  role,
  showInquiry,
}: {
  embedded?: boolean;
  role?: AppRole | null;
  showInquiry?: boolean;
} = {}) {
  const footer = <WireframeFooter role={role} showInquiry={showInquiry} />;
  if (embedded) return footer;

  return (
    <WireframeInteractiveShell includeBackToTop={false}>
      <div className="wireframe-footer-shell">{footer}</div>
    </WireframeInteractiveShell>
  );
}
